/**
 * Every SEO-facing string and tag on the site, in one place.
 *
 * Routes call `seo()` from their `head()` and get back the whole set: title,
 * description, Open Graph, Twitter card, robots and canonical.
 *
 * The canonical is emitted per route rather than once in `__root`, because
 * TanStack concatenates `links` across all matched routes without deduping by
 * `rel`. A root-level canonical would therefore render *alongside* each page's
 * own, and a page with two conflicting canonicals gets both ignored.
 */

import { TWITTER_HANDLE, YOUTUBE_URL } from './constants'
import type { DetailedHTMLProps, MetaHTMLAttributes } from 'react'

export const SITE_URL = 'https://controlstack.ai'
export const SITE_NAME = 'ControlStack.ai'
export const LEGAL_ENTITY = 'Daza Limited'
export const AUTHOR_NAME = 'Darren Hinde'
export const GITHUB_URL = 'https://github.com/darrenhinde'
export const TWITTER_URL = `https://twitter.com/${TWITTER_HANDLE.replace('@', '')}`

/**
 * One description, used for meta, Open Graph and Twitter.
 *
 * Keep it under ~160 characters: Google truncates the snippet around there,
 * and the lead clause ("Bring a codebase, a plan, or an AI setup...") is the
 * positioning — it has to survive the cut, so it goes first rather than last. It describes the
 * offer, not a content library — guides and resources are unpublished, so
 * promising them in a search snippet would send people to a redirect.
 */
export const SITE_DESCRIPTION =
  'Bring a codebase, a plan, or an AI setup that keeps going wrong. A 90-minute 1:1 on the context files, instructions and review loop that make agents drift.'

/**
 * 1200x630, so the card renders as `summary_large_image`. Regenerate with
 * `scripts/generate-og-image.mjs` after changing the headline or brand colours.
 */
export const OG_IMAGE = `${SITE_URL}/og-image.png`
export const OG_IMAGE_ALT =
  'ControlStack.ai — the AI writes the code. Keeping it maintainable is still your job.'

/** Absolute URL for a route path. Root stays bare, everything else loses any trailing slash. */
export function canonicalUrl(path: string): string {
  if (path === '/' || path === '') return SITE_URL
  return `${SITE_URL}/${path.replace(/^\/+|\/+$/g, '')}`
}

type MetaTag = DetailedHTMLProps<
  MetaHTMLAttributes<HTMLMetaElement>,
  HTMLMetaElement
>

/** Anything `JSON.stringify` can render losslessly. See `ldJson` for why. */
type JsonLdValue =
  | string
  | number
  | boolean
  | null
  | Array<JsonLdValue>
  | { [key: string]: JsonLdValue | undefined }

export type JsonLdDocument = { [key: string]: JsonLdValue | undefined }

/**
 * TanStack's head runtime special-cases a meta entry keyed `script:ld+json`
 * and renders it as a `<script type="application/ld+json">` in the head,
 * JSON-escaped, during SSR (see `headContentUtils` in
 * @tanstack/react-router). `MetaDescriptor` in router-core describes exactly
 * this key, but `head()`'s own option type narrows `meta` to React's
 * `MetaHTMLAttributes`, so the entry will not typecheck without a cast.
 *
 * That cast is why `JsonLdDocument` exists. The runtime wraps its
 * `JSON.stringify` in a bare `try {} catch {}` and silently DROPS the tag on
 * failure — so a `Date`, a `Map` or a circular reference inside a schema
 * object would make the whole block vanish from the page with no error
 * anywhere. Constraining the input to JSON-serialisable values turns that
 * into a compile error instead.
 */
function ldJson(doc: JsonLdDocument): MetaTag {
  return { 'script:ld+json': doc } as MetaTag
}

type SeoOptions = {
  /** Full <title>, including the brand suffix. */
  title: string
  description: string
  /** Route path, e.g. '/call'. Used for og:url and the canonical. */
  path: string
  /** Share-card headline, when it should differ from the <title>. */
  socialTitle?: string
  /** Keep the page out of the index — thin or post-conversion pages only. */
  noindex?: boolean
  /** Additional JSON-LD documents to embed in the head. */
  jsonLd?: Array<JsonLdDocument>
}

/**
 * Returns the `meta` and `links` arrays for a route's `head()`.
 *
 * Child meta wins over parent meta because TanStack walks matches deepest
 * first and keeps the first value it sees for a given name/property, so
 * repeating the og/twitter tags here is what actually overrides `__root`.
 */
export function seo({
  title,
  description,
  path,
  socialTitle,
  noindex = false,
  jsonLd = [],
}: SeoOptions) {
  const url = canonicalUrl(path)
  const cardTitle = socialTitle ?? title

  return {
    meta: [
      { title },
      { name: 'description', content: description },
      {
        name: 'robots',
        // `follow` either way. `noindex` alone keeps the page itself out of
        // the index; adding `nofollow` would also stop crawlers following the
        // links *on* it, which on /thank-you means the links back to the
        // homepage and the YouTube channel. There is no reason to suppress
        // those, and Google's own guidance for thin transactional pages is
        // noindex + follow.
        content: noindex
          ? 'noindex, follow'
          : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
      },

      // Open Graph
      { property: 'og:site_name', content: SITE_NAME },
      { property: 'og:locale', content: 'en_GB' },
      { property: 'og:type', content: 'website' },
      { property: 'og:title', content: cardTitle },
      { property: 'og:description', content: description },
      { property: 'og:url', content: url },
      { property: 'og:image', content: OG_IMAGE },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' },
      { property: 'og:image:alt', content: OG_IMAGE_ALT },

      // Twitter
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:site', content: TWITTER_HANDLE },
      { name: 'twitter:creator', content: TWITTER_HANDLE },
      { name: 'twitter:title', content: cardTitle },
      { name: 'twitter:description', content: description },
      { name: 'twitter:image', content: OG_IMAGE },
      { name: 'twitter:image:alt', content: OG_IMAGE_ALT },

      ...jsonLd.map(ldJson),
    ],
    links: [{ rel: 'canonical', href: url }],
  }
}

/**
 * Site-wide identity. Lives on the homepage only — repeating Organization on
 * every page adds bytes without adding meaning, and Google consolidates to the
 * homepage entity anyway.
 */
export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': `${SITE_URL}/#organization`,
  name: SITE_NAME,
  legalName: LEGAL_ENTITY,
  url: SITE_URL,
  logo: {
    '@type': 'ImageObject',
    url: `${SITE_URL}/android-chrome-512x512.png`,
    width: 512,
    height: 512,
  },
  description: SITE_DESCRIPTION,
  founder: { '@id': `${SITE_URL}/#darren` },
  sameAs: [YOUTUBE_URL, TWITTER_URL, GITHUB_URL],
}

export const personSchema = {
  '@context': 'https://schema.org',
  '@type': 'Person',
  '@id': `${SITE_URL}/#darren`,
  name: AUTHOR_NAME,
  url: SITE_URL,
  jobTitle: 'Software engineer',
  description:
    'Builds AI coding workflows and the open-source OpenAgents Control framework, and teaches them on the DarrenBuildsAI channel.',
  knowsAbout: [
    'AI-assisted software development',
    'Claude Code',
    'Cursor',
    'OpenCode',
    'AI agent context engineering',
    'Code review workflows',
  ],
  sameAs: [YOUTUBE_URL, TWITTER_URL, GITHUB_URL],
}

export const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${SITE_URL}/#website`,
  name: SITE_NAME,
  url: SITE_URL,
  description: SITE_DESCRIPTION,
  inLanguage: 'en-GB',
  publisher: { '@id': `${SITE_URL}/#organization` },
}

/** Breadcrumbs for any one-level-deep page. */
export function breadcrumbSchema(name: string, path: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name, item: canonicalUrl(path) },
    ],
  }
}

/**
 * Every URL that belongs in the sitemap, in one list.
 *
 * `noindex` pages (/thank-you) and the retired 301s (/coaching, /guides,
 * /resources) are deliberately absent — a sitemap that lists redirects or
 * noindex URLs makes Search Console report coverage errors against you.
 *
 * `lastmod` is a real date, edited when the page's content actually changes.
 * Stamping it with `new Date()` on every request would be a lie, and Google
 * discounts a lastmod it can see is always "today".
 */
export const SITEMAP_PAGES = [
  { path: '/', lastmod: '2026-09-13', changefreq: 'weekly', priority: '1.0' },
  {
    path: '/call',
    lastmod: '2026-09-13',
    changefreq: 'monthly',
    priority: '0.9',
  },
  {
    path: '/privacy',
    lastmod: '2026-01-23',
    changefreq: 'yearly',
    priority: '0.2',
  },
  {
    path: '/terms',
    lastmod: '2026-01-12',
    changefreq: 'yearly',
    priority: '0.2',
  },
  {
    path: '/cookies',
    lastmod: '2026-01-12',
    changefreq: 'yearly',
    priority: '0.2',
  },
] as const

/**
 * A bare `&` in a <loc> makes the ENTIRE sitemap unparseable — Search Console
 * rejects the whole file, not the one URL — so escape even though every path
 * in SITEMAP_PAGES is a safe literal today.
 */
function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')
}

export function buildSitemapXml(): string {
  const urls = SITEMAP_PAGES.map(
    ({ path, lastmod, changefreq, priority }) => `  <url>
    <loc>${escapeXml(canonicalUrl(path))}</loc>
    <lastmod>${lastmod}</lastmod>
    <changefreq>${changefreq}</changefreq>
    <priority>${priority}</priority>
  </url>`,
  ).join('\n')

  return `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
${urls}
</urlset>
`
}
